import * as Yup from 'yup';
import { showResult } from 'utils';
import { generateFullName } from 'utils/badgeHelpers';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  API_PATIENT_NOTES,
  API_PATIENT_NOTE_CREATE,
} from 'services/dataService/ccmResources';
import useTable from 'utils/useTable';
import useValidation from 'utils/useValidation';
import { useParams } from 'react-router-dom';
import useConfirm from 'utils/useConfirm';
import { format } from 'date-fns';
import { del } from 'idb-keyval';
import useCacheValue from './useCacheValue';

const VALIDATION_RULES = Yup.object().shape({
  note: Yup.string().min(50).required('required'),
});

const generateSaveConfirmMessage = (patient, riskScore) => {
  const { birthDate, title, firstName, middleName, lastName } = patient;
  const fullName = generateFullName(title, firstName, middleName, lastName);
  const dob = birthDate?.replaceAll('-', '/');

  const messageArray = [
    'Further edits will not be allowed once saved.',
    'Confirm that you would like to save this note for ',
    `${fullName}`,
    `DOB: ${dob}`,
    `Risk Score:${riskScore}`,
  ];
  const message = messageArray.join('\n');
  return message;
};

const DATE_FORMAT = 'yyyy-MM-dd HH:mm:s';
export const PER_PAGE = 5;
const createRow = () => ({ note: '' });

const showCancelConfirm = useConfirm({
  message: 'You have unsaved changes, are you sure you want to cancel ?',
  title: 'Confirm',
});

const getCacheId = (patientGuid) => `ccmNarrativeForm-${patientGuid}`;

const useClinicalNotes = () => {
  const [narrativeModalOpen, setNarrativeModalOpen] = useState(false);
  const { patientGuid } = useParams();
  const dates = useSelector((state) => state.ccmPatientDetails.dates);
  const patientRiskScore = useSelector(
    (state) => state.ccmPatientDetails.questions.score,
  );
  const patient = useSelector((state) => state.ccmPatientDetails.patient);
  const { row, setRow, isValid, validate, messageErrors } = useValidation(
    {},
    VALIDATION_RULES,
  );
  const cacheId = `ccmNotesInput-${patientGuid}`;

  const { cacheLoading } = useCacheValue({
    value: row,
    setValue: setRow,
    cacheId,
  });

  const createNewRow = () => {
    setRow(createRow());
  };
  const clearRows = () => setRow({});
  const {
    data: { items },
    count,
    loading,
    page,
    setPage,
    setFilters,
    saveNewRow,
  } = useTable(
    API_PATIENT_NOTES,
    PER_PAGE,
    { patientGuid },
    {
      makeInitialRequest: false,
    },
  );

  useEffect(() => {
    if (dates.length > 1) {
      const startTime = format(dates[0], DATE_FORMAT);
      const endTime = format(dates[1], DATE_FORMAT);
      setFilters({ patientGuid, startTime, endTime });
    }
    setNarrativeModalOpen(false);
    clearRows();
  }, [dates, patientGuid]);

  const save = async () => {
    const result = await validate();
    if (!result.isValid) return;

    const showSaveConfirm = useConfirm({
      message: generateSaveConfirmMessage(patient.profile, patientRiskScore),
      title: 'Are you sure you want to save this note?',
    });

    const confirm = await showSaveConfirm();
    if (!confirm) return;

    const { error } = await saveNewRow(API_PATIENT_NOTE_CREATE, {
      ...row,
      patientGuid,
    });

    if (error) showResult(error);
    else showResult();
    clearRows();
  };

  const cancel = async () => {
    if (row.note) {
      const confirm = await showCancelConfirm();
      if (!confirm) return;
    }
    clearRows();
    setNarrativeModalOpen(false);
    del(getCacheId(patientGuid));
  };

  return {
    narrativeModalOpen,
    setNarrativeModalOpen,
    patientGuid,
    row,
    setRow,
    loading,
    cacheLoading,
    items,
    isValid,
    messageErrors,
    createNewRow,
    setPage,
    count,
    page,
    save,
    cancel,
  };
};

export default useClinicalNotes;
